import { inferRecord, TupleToUnion } from "@base/util/TypeUtil";
import { BeosCountryCode, PartListGroup, Unit } from "@biz/Definitions";
import { Access } from "@biz/definitions/AccessDefinitions";
import {
  AdditionalArticleInfosOption, ArticleAvailability, ArticleListType, IArticle, IArticleAffiliation, IArticleComputedInfo, IArticleImage, ICustomArticleInfo, IRuleArticle, OfferListType, RuleArticleMap, ShoppingArticleType
} from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureStore, IArticleFeatureTemplate } from "@biz/definitions/ArticleFeatureDefinitions";
import { DiscountGroups } from "@biz/definitions/DatabaseDefinitions";
import { IPowderCoatingProduct } from "@biz/definitions/PowderCoatingDefinitions";
import { ICurrencyData, PriceListIndex } from "@biz/definitions/PriceDefinitions";
import { INotesAndFiles } from "@biz/definitions/ProductDefinitions";
import { ICustomArticle } from "@biz/definitions/UserProfileDefinitions";
import { ILocaContext } from "@biz/language/LocaContext";
import { BeosVarianten } from "@biz/orders/BeosVarianten";
import { IColorItem } from "@biz/SunSailColors";

// the shopping list containing the articles the user wishes...


export type IShoppingListItems = Record<string, IShoppingListItem>;
// export type IShoppingListItems = Record<string, IShoppingListItem[]>;

export interface IShoppingList {
  items: IShoppingListItems;
  notesAndFiles?: INotesAndFiles;
}

export interface IShoppingListItem {
  // id: string; // don't use id to avoid duplication of ids in key and value
  articleNumber: string;
  count?: number;
  countClientAvailable?: number;
  mul?: number;
  features?: ArticleFeatureStore;

  affiliation?: IArticleAffiliation;
  powderCoatingIndex?: number;
  addedSlotTime: number;
}

// export type QuickCategory = "customArticles" | "wallmount" | "heightChange" | "coilUp" | "motor" | "remote" | "remote+" | "poleClamp" | "earthScrew";
export const quickCategoryOrder = {
  motor: 10,
  remoteReceiver: 15,
  motorAdditional: 20,
  windSensor: 25,
  "remote+": 30,
  remote: 40,
  wallmount: 50,
  poleClamp: 60,
  earthScrew: 70,
  heightChange: 80,
  coilUp: 90,
  noCategory: 1000,
  miniCart: 2000,
  curtainTracks: 50,
  curtainRopes: 50,
  curtainMount: 100,
  curtainAdditional: 2000,
  customArticles: 10000
}

export type QuickCategory = keyof typeof quickCategoryOrder;

const QuickOverlayPriorityGroups = inferRecord<number>()({
  protectiveCover: 10,
  cornerProtection: 20,
  "99STATIK": 30,
  ropeConserver: 1000, // Seil Konservierer
  algaeRemover: 1015,
  DrillTemplate: 2000, // Bohrschablonen
  SunsailBag: 3000, // Segeltaschen
})

export const QuickOverlayPriority = inferRecord<number>()({
  ...QuickOverlayPriorityGroups,
  "00E13000816": QuickOverlayPriorityGroups.ropeConserver,
  "00E13001405": QuickOverlayPriorityGroups.ropeConserver,

  "00E13001325": 1010, // Soliday Reinigungs und Pflegset für Soliday C

  "00E13001326": QuickOverlayPriorityGroups.algaeRemover,
  "00E13001252": QuickOverlayPriorityGroups.algaeRemover,

  "00E13001011": 1020, // Soliday Cleaner Oxidationsreiniger
  "00E13001010": 1030, // Soliday Easy-Top 1000ml
  "00E13001986": 1033,
  "00E13001987": 1036,
  "00E13001988": 1039,

  "00E13001156": QuickOverlayPriorityGroups.DrillTemplate,
  "00E13001880": QuickOverlayPriorityGroups.DrillTemplate,
  "00E13001881": QuickOverlayPriorityGroups.DrillTemplate,
  "00E13002062": QuickOverlayPriorityGroups.DrillTemplate,

  "00E13000827": QuickOverlayPriorityGroups.SunsailBag, // Segeltaschen
  "00E13000828": QuickOverlayPriorityGroups.SunsailBag, // Segeltaschen
  "00E13000829": QuickOverlayPriorityGroups.SunsailBag, // Segeltaschen
  "00E13000843": QuickOverlayPriorityGroups.SunsailBag, // Segeltaschen
})

export function getQuickOverlayPriority(articleNumber: string): number | undefined {
  const priority = (QuickOverlayPriority as any)[articleNumber];
  if (priority !== undefined)
    return priority;
  return undefined;
}



// Sandy: https://plaspack.atlassian.net/browse/API-91
export const ShoppingCartPriority = {
  SojoHW: -10,
  SojoCloth: -9,
  None: 0,
  Motor: 10,
  MotorAxle: 20,
  MotorCable: 30,
  MotorDesc: 40,
  PreMontage: 50,

  Pole: 1000,
  // "60001B001": 1000, // https://plaspack.atlassian.net/browse/API-91
  // "00E13000235": 1000, // https://plaspack.atlassian.net/browse/API-91

  PoleDrill: 1001,
  PoleBend: 1002,
  PoleCap: 1003,
  // "99BOHRUNG001": 1001, // https://plaspack.atlassian.net/browse/API-91

  PoleMount: 1003,
  // "60004A001": 1010, // https://plaspack.atlassian.net/browse/API-91
  // "00E13001970": 1010, // https://plaspack.atlassian.net/browse/API-91

  PoleHeightChange: 1020,
  // "60003A001": 1020, // https://plaspack.atlassian.net/browse/API-91

  // Bolzen
  "60003A004": 1030, // https://plaspack.atlassian.net/browse/API-91
  "00E13000127": 1030, // https://plaspack.atlassian.net/browse/API-91

  // Seilrollen
  "00E13000042": 1040, // https://plaspack.atlassian.net/browse/API-91

  PoleKlampe: 1050,
  // "00E13000522": 1050, // https://plaspack.atlassian.net/browse/API-91
  // "00E13001414": 1050, // https://plaspack.atlassian.net/browse/API-91
  END: 100000
} satisfies Record<string, number>

export const PartListPriority = {
  SojoHW: -10,
  SojoCloth: -9,
  sail: 0,
  SystemPackaging: 1,
  Umlaufroller: 10,
  RevolvingRope: 11,
  Wirbel: 12,
  heightChange: 1000,
  heightChangeAdditional: 1001,
  coilUpSecurity: 2000
}


export const articleGroups = ["miniCart", "twistingSet", "indirectPylon", "rope1", "twistingSet", "system", "sail", "curtainCloth", "curtainSlide", "curtainRope", "curtainArc", "curtainRopeMount", "curtainSuspension", "curtainAdditional", "end", "hidden"] as const;
export type ArticleGroup = TupleToUnion<typeof articleGroups>;
export type BeosOrderAnzahlCalculationType = "count_mul" | "count" | "mul" | "laufMeter" | "quadratMeter";
export interface IBeosOrderProperties {
  laufMeter?: number;
  einheit?: string;
  quadratMeter?: number;
  varianten?: BeosVarianten;
  anzahlCalculationType?: BeosOrderAnzahlCalculationType;
}

export interface IPartListProperties {
  list?: ArticleListType | "none";
  group?: PartListGroup;
  priority?: number; // (PartListPriority as any)[articles[0].articleNumber]
  name?: string;
}

export type IShoppingArticleNewIdFunction = (articleNumber: string) => string;
export interface IShoppingArticleOptions {
  parentArticle?: IRuleArticle

  idMode: "shopping" | "customArticle" | "boxed";
  idCategory?: string;

  count: number;
  mul?: number;
  granularity: number;
  maxUnits?: number;
  articleType?: ShoppingArticleType;
  additionalArticleInfo?: AdditionalArticleInfosOption;
  affiliation?: IArticleAffiliation,

  priceListIndex?: PriceListIndex;
  overridePrice?: number;

  stkUnit?: Unit;
  mulUnit?: Unit;

  offerList?: OfferListType;
  articleGroup?: ArticleGroup; // // Warenkorb App Katgegorie
  shoppingCartPriority?: number;
  quickCategory?: QuickCategory; // QuickOverlay category
  quickOverlayPriority?: number;

  features?: ArticleFeatureStore;
  featureTemplate?: IArticleFeatureTemplate;

  computed?: IArticleComputedInfo;
  partList?: IPartListProperties;
  beosOrder?: IBeosOrderProperties;
  customArticle?: ICustomArticleInfo;

  disabled?: boolean;
  addToCart?: boolean;
}

/*export interface IShoppingArticleOptionsOld {
  count: number;
  mul?: number;
  articleType?: ShoppingArticleType;
  stkUnit?: Unit;
  mulUnit?: Unit;
  granularity: number;
  maxUnits?: number;
  affiliation?: IArticleAffiliation,
  partList?: IPartListProperties;
  priceListIndex: PriceListIndex;
  quickCategory?: QuickCategory;
  quickOverlayPriority?: number;
  shoppingCartPriority?: number;
  offerList?: OfferListType;
  articleGroup?: ArticleGroup;
  additionalArticleInfo?: Record<string, IAdditionalArticleInfoItem | AdditionalArticleInfoItemFn | undefined | false>;
  idComplete?: string;
  idPart?: string;
  idAdd?: string;
  idCategory?: string;
  customArticle?: IRuleCustomArticle;
  computed?: IArticleComputedInfo;
  beosOrder?: IBeosOrderProperties;
  hideForOffer?: boolean;
  disabled?: boolean;
  features?: ArticleFeatureStore;
  featureTemplate?: IArticleFeatureTemplate;
  addToCart?: boolean;
}*/

export interface IShoppingListContext {
  orderTime: number;
  articleMap: Record<string, IArticle>;
  colorMap: Record<string, IColorItem>;
  company: {
    language: string;
    surcharge: number;
    countryCode: BeosCountryCode | undefined;
    discountGroups: DiscountGroups;
  },
  currency: ICurrencyData;
  priceListIndex: PriceListIndex;
  lc: ILocaContext;
  wishList: IShoppingListItems;
  customArticles: Record<string, ICustomArticle>;
  powderCoating: IPowderCoatingProduct | undefined;

  missingArticles: string[];
  ruleArticles: RuleArticleMap;
}


export interface IShopItem {
  articleNumber: string;
  maxUnits: number;
  granularity: number;
  access?: Access;
  availability?: ArticleAvailability;
  badgeText?: string;
  images?: IArticleImage[];
}